import { Button, Input, MagellanPopup } from 'components/atoms';
import ArtistSearchCard from 'components/molecules/card/artist-search/ArtistSearchCard';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowRight } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetArtistListInfinite } from 'services/artists';
import { IArtistResponse } from 'services/artists/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

interface IArtistSelectPopupFormProps {
  trigger?: JSX.Element | ((isOpen: boolean) => JSX.Element) | undefined;
  onSelectArtist?: (artist: IArtistResponse, actions: PopupActions) => void;
  disabledArtists?: string[];
  onCreateNewArtist?: () => void;
  mode?: 'band' | 'release';
}

const ArtistSelectPopupForm = ({
  trigger,
  onSelectArtist,
  disabledArtists,
  onCreateNewArtist,
  mode = 'release',
}: IArtistSelectPopupFormProps) => {
  const popupRef = useRef<PopupActions | null>(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const [artistSearch, setArtistSearch] = useState('');
  const [artist, setArtist] = useState<IArtistResponse | null>(null);
  const artists = useGetArtistListInfinite({ itemCount: 10 }, open);

  return (
    <Popup
      ref={popupRef}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
        setArtist(null);
        setArtistSearch('');
      }}
      modal
      nested
      trigger={trigger}
    >
      <MagellanPopup>
        <MagellanPopup.Header>
          {mode === 'release' && t('button.add-artist-to-release')}
          {mode === 'band' && t('button.add-artist-to-band')}
        </MagellanPopup.Header>

        <Input.Text
          value={artistSearch}
          onChange={(e) => setArtistSearch(e.target.value)}
          name=""
          appBackground
          placeholder={t('input.placeholder.search-artist')}
        />
        <div className="flex flex-col w-full gap-4 max-w-96 overflow-auto max-h-[450px] [scrollbar-width:none] ">
          {onCreateNewArtist && mode === 'release' && (
            <ArtistSearchCard onClick={onCreateNewArtist} />
          )}
          {artists.data?.pages
            .flat()
            .filter((a) => !disabledArtists?.includes(a.id))
            .map((a) => (
              <ArtistSearchCard
                key={a.id}
                artist={a}
                isSelected={a.id === artist?.id}
                onClick={() => setArtist(a.id === artist?.id ? null : a)}
              />
            ))}
        </div>
        <div className="flex justify-between w-full">
          <Button isClear onClick={() => popupRef.current?.close()}>
            <Button.Text text={t('button.cancel')} />
          </Button>
          <Button
            variant={mode === 'band' ? BUTTON_VARIANT.submit : BUTTON_VARIANT.background}
            disabled={!artist}
            onClick={() =>
              artist &&
              onSelectArtist &&
              popupRef.current &&
              onSelectArtist(artist, popupRef.current)
            }
          >
            {mode === 'band' && <Button.Text text={t('button.add')} />}
            {mode === 'release' && <Button.Text text={t('button.select-role')} />}
            {mode === 'release' && <Button.Icon icon={<PiArrowRight />} />}
          </Button>
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default ArtistSelectPopupForm;
